// Day-of-Exam Prep Packet
// Single-purpose PDF to bring to a C&P exam or VA appointment.
// Cover • Today's appointment details • Veteran summary • Conditions overview
// • Per-condition deep dive (claimed-as, theory, est rating, functional impact,
//   strongest evidence, C&P prep checklist) • Active medications
// • Questions to ask + things to bring

import { PDFDocument, StandardFonts, rgb, degrees } from "pdf-lib";
import {
  computePillarStatus,
} from "./claimUtils";
import { evidenceElements, bodySystems, getConditionResources } from "./claimData";
import { appointmentTypeMeta, rxNextRefillDate } from "./reminderUtils";
import { THEORIES } from "./claimEnums";

const BRAND_NAME = "BeTheNexus.com";
const PAGE_W = 612;
const PAGE_H = 792;
const M = 48;

const C_BRAND = rgb(0.11, 0.30, 0.85);
const C_BRAND_DARK = rgb(0.06, 0.09, 0.16);
const C_TEXT = rgb(0.06, 0.09, 0.16);
const C_MUTED = rgb(0.39, 0.45, 0.55);
const C_BORDER = rgb(0.85, 0.88, 0.92);
const C_BG_SOFT = rgb(0.97, 0.98, 0.99);
const C_RED = rgb(0.60, 0.10, 0.10);
const C_AMBER = rgb(0.57, 0.27, 0.05);
const C_BLUE = rgb(0.12, 0.25, 0.62);
const C_GREEN = rgb(0.02, 0.37, 0.27);
const STATUS_COLOR = { missing: C_RED, weak: C_AMBER, adequate: C_BLUE, strong: C_GREEN };
const STATUS_BG = {
  missing: rgb(0.99, 0.94, 0.94),
  weak: rgb(0.99, 0.97, 0.91),
  adequate: rgb(0.93, 0.96, 1.0),
  strong: rgb(0.92, 0.99, 0.95),
};

function sanitizeText(text) {
  return String(text ?? "")
    .replace(/[\u2018\u2019\u201A\u201B]/g, "'")
    .replace(/[\u201C\u201D\u201E\u201F]/g, '"')
    .replace(/[\u2013\u2014]/g, "-")
    .replace(/\u2026/g, "...")
    .replace(/[\u2022\u00B7]/g, "*")
    .replace(/\u00A0/g, " ")
    .replace(/[^\x09\x0A\x0D\x20-\x7E\xA0-\xFF]/g, "");
}

function wrap(text, charsPerLine) {
  return String(text || "").replace(/\r\n?/g, "\n").split("\n").flatMap((para, idx, arr) => {
    const items = para.split(" ").filter(Boolean);
    const rows = [];
    let row = "";
    items.forEach((w) => {
      if ((row + " " + w).trim().length > charsPerLine) { rows.push(row); row = w; }
      else row = (row + " " + w).trim();
    });
    if (row) rows.push(row);
    if (rows.length === 0) rows.push("");
    if (idx < arr.length - 1) rows.push("");
    return rows;
  });
}

class Canvas {
  constructor(doc, font, bold) {
    this.doc = doc; this.font = font; this.bold = bold; this.page = null; this.y = 0; this.section = "";
    this.pages = [];
  }
  newPage(section) {
    this.page = this.doc.addPage([PAGE_W, PAGE_H]);
    this.pages.push({ page: this.page, section: section ?? this.section });
    this.section = section ?? this.section;
    this.y = PAGE_H - M;
    return this.page;
  }
  ensure(space, section) { if (!this.page || this.y - space < M + 30) this.newPage(section); }
  text(value, x, y, opts = {}) {
    const { size = 10, bold = false, color = C_TEXT, font, maxWidth } = opts;
    const useFont = font || (bold ? this.bold : this.font);
    let str = sanitizeText(value);
    if (maxWidth && str) {
      let safety = 200;
      while (safety-- > 0 && str.length > 1 && useFont.widthOfTextAtSize(str, size) > maxWidth) str = str.slice(0, -3);
      if (str !== sanitizeText(value)) str = str.replace(/\.+$/, "") + "...";
    }
    this.page.drawText(str, { x, y, size, font: useFont, color });
  }
  line(x1, y1, x2, y2, color = C_BORDER, thickness = 0.7) {
    this.page.drawLine({ start: { x: x1, y: y1 }, end: { x: x2, y: y2 }, thickness, color });
  }
  rect(x, y, w, h, opts = {}) {
    const { fill, stroke, strokeW = 0.7 } = opts;
    this.page.drawRectangle({ x, y, width: w, height: h, color: fill, borderColor: stroke, borderWidth: stroke ? strokeW : 0 });
  }
  heading(label, color = C_BRAND_DARK) {
    this.ensure(40);
    this.rect(M, this.y - 4, PAGE_W - M * 2, 1.4, { fill: color });
    this.y -= 18;
    this.text(label, M, this.y, { size: 13, bold: true, color });
    this.y -= 18;
  }
  sectionTitle(label) {
    this.ensure(26);
    this.text(label.toUpperCase(), M, this.y, { size: 9, bold: true, color: C_BRAND });
    this.y -= 12;
    this.line(M, this.y + 4, M + 60, this.y + 4, C_BRAND, 1.2);
    this.y -= 6;
  }
  para(text, opts = {}) {
    const { size = 10, color = C_TEXT, width = 95, indent = 0, bold = false } = opts;
    wrap(text, width).forEach((line) => {
      this.ensure(size + 4);
      this.text(line, M + indent, this.y, { size, color, bold });
      this.y -= size + 3;
    });
  }
  bullet(text, opts = {}) {
    const { size = 9, color = C_TEXT } = opts;
    this.ensure(size + 4);
    this.text("\u2022", M + 8, this.y, { size, color: C_BRAND, bold: true });
    wrap(text, 92).forEach((line, idx) => {
      if (idx > 0) this.ensure(size + 3);
      this.text(line, M + 20, this.y, { size, color });
      this.y -= size + 3;
    });
  }
  statusPill(text, status, x, y) {
    const fontSize = 8;
    const padX = 6;
    const w = this.bold.widthOfTextAtSize(text, fontSize) + padX * 2;
    this.page.drawRectangle({ x, y: y - 2, width: w, height: 13, color: STATUS_BG[status], borderColor: STATUS_COLOR[status], borderWidth: 0.6 });
    this.text(text, x + padX, y + 1, { size: fontSize, bold: true, color: STATUS_COLOR[status] });
    return w;
  }
}

function drawHeader(canvas, info) {
  canvas.page.drawRectangle({ x: 0, y: PAGE_H - 28, width: PAGE_W, height: 28, color: C_BRAND_DARK });
  canvas.page.drawRectangle({ x: 0, y: PAGE_H - 30, width: PAGE_W, height: 2, color: C_BRAND });
  canvas.page.drawRectangle({ x: M, y: PAGE_H - 22, width: 18, height: 14, color: C_BRAND });
  canvas.text("BN", M + 4, PAGE_H - 19, { size: 8, bold: true, color: rgb(1, 1, 1) });
  canvas.text(BRAND_NAME, M + 24, PAGE_H - 19, { size: 9, bold: true, color: rgb(1, 1, 1) });
  canvas.text(canvas.section || "Exam Packet", PAGE_W - M - 200, PAGE_H - 19, { size: 8, color: rgb(0.78, 0.83, 0.92) });
  canvas.text(info.title || "Day-of-Exam Packet", PAGE_W - M - canvas.font.widthOfTextAtSize(sanitizeText(info.title || "Day-of-Exam Packet"), 8), PAGE_H - 19, { size: 8, color: rgb(1, 1, 1) });
}

function drawFooter(canvas, pageNum, total) {
  canvas.page.drawRectangle({ x: 0, y: 0, width: PAGE_W, height: 22, color: C_BG_SOFT });
  canvas.page.drawRectangle({ x: 0, y: 22, width: PAGE_W, height: 1, color: C_BORDER });
  canvas.text("Day-of-Exam Packet * Take this with you * NOT an official VA document", M, 9, { size: 7, color: C_MUTED });
  canvas.text(`Page ${pageNum} of ${total}`, PAGE_W - M - 50, 9, { size: 7, color: C_MUTED, bold: true });
}

function drawWatermark(canvas, isFree) {
  if (!isFree) return;
  canvas.page.drawText("FREE - UPGRADE TO REMOVE", {
    x: 30, y: 380, size: 42,
    font: canvas.bold, color: rgb(0.92, 0.62, 0.62),
    rotate: degrees(34), opacity: 0.5,
  });
}

// === COVER ===
function drawCover(canvas, info) {
  canvas.newPage("Cover");
  // Big banner
  canvas.page.drawRectangle({ x: 0, y: PAGE_H - 220, width: PAGE_W, height: 220, color: C_BRAND_DARK });
  canvas.page.drawRectangle({ x: 0, y: PAGE_H - 222, width: PAGE_W, height: 4, color: C_BRAND });
  canvas.text("DAY-OF-EXAM PACKET", M, PAGE_H - 90, { size: 26, bold: true, color: rgb(1, 1, 1) });
  canvas.text("Bring this to your appointment.", M, PAGE_H - 118, { size: 12, color: rgb(0.85, 0.88, 0.94) });

  const a = info.appointment;
  if (a) {
    const dt = a.datetime ? new Date(a.datetime) : null;
    const meta = appointmentTypeMeta(a.type);
    canvas.text(meta.label.toUpperCase(), M, PAGE_H - 158, { size: 10, bold: true, color: C_BRAND });
    canvas.text(a.title || "Appointment", M, PAGE_H - 178, { size: 18, bold: true, color: rgb(1, 1, 1) });
    if (dt) {
      canvas.text(`${dt.toLocaleDateString(undefined, { weekday: "long", year: "numeric", month: "long", day: "numeric" })} at ${dt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`,
        M, PAGE_H - 198, { size: 11, color: rgb(0.85, 0.88, 0.94) });
    }
  }

  canvas.y = PAGE_H - 260;

  // Veteran summary box
  canvas.rect(M, canvas.y - 88, PAGE_W - M * 2, 88, { fill: C_BG_SOFT, stroke: C_BORDER });
  canvas.text("VETERAN", M + 12, canvas.y - 14, { size: 9, bold: true, color: C_BRAND });
  const p = info.profile || {};
  const name = sanitizeText(p.fullName || p.preferredName || "(Name not provided)");
  canvas.text(name, M + 12, canvas.y - 32, { size: 14, bold: true });
  const subParts = [];
  if (p.branch) subParts.push(p.branch);
  if (p.serviceDates) subParts.push(p.serviceDates);
  if (p.fileNumber) subParts.push(`File #: ${p.fileNumber}`);
  if (subParts.length) canvas.text(subParts.join("  *  "), M + 12, canvas.y - 50, { size: 10, color: C_MUTED });
  const wsLabel = info.workspaceTitle ? `Claim workspace: ${info.workspaceTitle}` : "";
  if (wsLabel) canvas.text(wsLabel, M + 12, canvas.y - 66, { size: 9, color: C_MUTED });
  canvas.y -= 100;

  // Appointment details box
  if (a) {
    canvas.rect(M, canvas.y - 110, PAGE_W - M * 2, 110, { fill: C_BG_SOFT, stroke: C_BORDER });
    canvas.text("APPOINTMENT DETAILS", M + 12, canvas.y - 14, { size: 9, bold: true, color: C_BRAND });
    let yy = canvas.y - 34;
    const rowItem = (label, value) => {
      if (!value) return;
      canvas.text(label.toUpperCase(), M + 12, yy, { size: 8, bold: true, color: C_MUTED });
      canvas.text(value, M + 140, yy, { size: 10, maxWidth: PAGE_W - M * 2 - 152 });
      yy -= 16;
    };
    rowItem("Provider", a.provider);
    rowItem("Location", a.location);
    rowItem("Remind days before", String(a.remindDaysBefore ?? 1));
    if (a.notes) rowItem("Notes", a.notes);
    canvas.y -= 124;
  }

  // Footer disclaimer
  canvas.text("NOT AN OFFICIAL VA DOCUMENT. PLANNING & PREPARATION TOOL ONLY.", M, 60, { size: 8, bold: true, color: C_MUTED });
  canvas.text("Generated by ClaimOps VA. Verify all rating criteria at VA.gov and eCFR.gov.", M, 48, { size: 8, color: C_MUTED });
}

// === CONDITIONS OVERVIEW ===
function drawConditionsOverview(canvas, conditions, evidence, waiverCtx) {
  canvas.newPage("Conditions Overview");
  canvas.heading("CONDITIONS BEING CLAIMED / DISCUSSED");
  canvas.para("This is what you and the VA examiner should be talking about today. Brief them on each.", { size: 9, color: C_MUTED });
  canvas.y -= 6;

  // Table header
  const cols = [
    { x: M, label: "DC" },
    { x: M + 60, label: "CLAIMED AS" },
    { x: M + 250, label: "THEORY" },
    { x: M + 360, label: "SIDE" },
    { x: M + 420, label: "EVIDENCE" },
  ];
  canvas.rect(M - 4, canvas.y - 14, PAGE_W - M * 2 + 8, 18, { fill: C_BRAND_DARK });
  cols.forEach((c) => canvas.text(c.label, c.x, canvas.y - 10, { size: 8, bold: true, color: rgb(1, 1, 1) }));
  canvas.y -= 22;

  conditions.forEach((issue, idx) => {
    canvas.ensure(40);
    if (idx % 2 === 0) canvas.rect(M - 4, canvas.y - 22, PAGE_W - M * 2 + 8, 26, { fill: C_BG_SOFT });
    canvas.text(`DC ${issue.code || "?"}`, M, canvas.y - 6, { size: 8, bold: true, maxWidth: 56 });
    canvas.text(issue.claimedAs || "(untitled)", M + 60, canvas.y - 6, { size: 9, bold: true, maxWidth: 186 });
    canvas.text(issue.theory || THEORIES.direct, M + 250, canvas.y - 6, { size: 8, maxWidth: 106 });
    canvas.text(issue.bilateralSide || "—", M + 360, canvas.y - 6, { size: 8, maxWidth: 56 });
    const evCount = evidence.filter((e) => e.issueIds?.includes(issue.id)).length;
    canvas.text(`${evCount} item${evCount === 1 ? "" : "s"}`, M + 420, canvas.y - 6, { size: 8, maxWidth: 70 });
    canvas.y -= 26;
  });

  if (conditions.length === 0) {
    canvas.y -= 6;
    canvas.para("No conditions currently linked to this appointment or in the workspace.", { size: 9, color: C_MUTED });
  }
}

// === PER-CONDITION DEEP DIVE ===
function drawConditionDetail(canvas, issue, evidence, waiverCtx) {
  canvas.newPage(`Condition: ${issue.claimedAs || issue.code}`);
  canvas.heading(`DC ${issue.code || "?"} - ${issue.claimedAs || "(untitled)"}`);

  const sysLabel = bodySystems.find((s) => s.id === issue.system)?.label || issue.system || "—";
  canvas.text(`Body system: ${sysLabel}`, M, canvas.y, { size: 9, color: C_MUTED });
  canvas.y -= 14;
  canvas.text(`Theory: ${issue.theory || THEORIES.direct}    *    Side: ${issue.bilateralSide || "Not applicable"}    *    Priority: ${issue.priority || "Medium"}`, M, canvas.y, { size: 9 });
  canvas.y -= 16;

  // Functional impact
  if (issue.functionalImpact) {
    canvas.sectionTitle("Functional impact (what to describe to the examiner)");
    canvas.para(issue.functionalImpact, { size: 10, width: 95 });
    canvas.y -= 4;
  }

  // Proof pillars w/ status
  canvas.sectionTitle("Proof pillars — current evidence status");
  let pillX = M;
  evidenceElements.forEach((el) => {
    const status = computePillarStatus(issue, el.id, evidence, waiverCtx);
    canvas.ensure(18);
    const w = canvas.statusPill(`${el.label}: ${status.toUpperCase()}`, status, pillX, canvas.y);
    pillX += w + 6;
    if (pillX > PAGE_W - M - 80) {
      pillX = M;
      canvas.y -= 18;
    }
  });
  canvas.y -= 22;

  // Strongest evidence per pillar
  canvas.sectionTitle("Top evidence you have");
  const issueEv = evidence.filter((ev) => ev.issueIds?.includes(issue.id));
  evidenceElements.forEach((el) => {
    const items = issueEv
      .filter((ev) => ev.element === el.id)
      .sort((a, b) => (b.date || "").localeCompare(a.date || ""))
      .slice(0, 2);
    if (items.length === 0) return;
    canvas.ensure(16);
    canvas.text(`${el.label}:`, M, canvas.y, { size: 9, bold: true, color: C_BRAND });
    canvas.y -= 12;
    items.forEach((ev) => {
      const summary = ev.summary ? ` — ${ev.summary}` : "";
      const dateStr = ev.date ? ` (${ev.date})` : "";
      canvas.bullet(`${ev.title || "Untitled evidence"}${dateStr}${summary}`, { size: 9 });
    });
  });

  // C&P prep checklist (from existing helper)
  const res = getConditionResources(issue.code, issue.system);
  // Strip any cpPrep bullet that mentions a specific rating percentage — those tell the examiner
  // what number the veteran is hoping for, and we don't want the packet to look like rating-fishing.
  const safeCpPrep = (res?.cpPrep || []).filter((item) => !/\d+\s*%/.test(item));
  if (safeCpPrep.length) {
    canvas.y -= 4;
    canvas.sectionTitle("C&P exam prep — what the examiner is looking for");
    safeCpPrep.forEach((item) => canvas.bullet(item, { size: 9 }));
  }

  // DBQ + CFR reference
  if (res?.dbqName || res?.cfrRef) {
    canvas.y -= 4;
    canvas.sectionTitle("Reference");
    if (res.dbqName) canvas.bullet(`DBQ: ${res.dbqName}`, { size: 9 });
    if (res.cfrRef) canvas.bullet(`CFR: ${res.cfrRef}`, { size: 9 });
  }
}

// === ACTIVE MEDICATIONS ===
function drawMedications(canvas, prescriptions) {
  const active = (prescriptions || []).filter((rx) => !rx.archived);
  if (active.length === 0) return;
  canvas.newPage("Active Medications");
  canvas.heading("ACTIVE MEDICATIONS");
  canvas.para("Tell the examiner about every medication you take. Side effects matter — make sure they are recorded.", { size: 9, color: C_MUTED });
  canvas.y -= 6;

  const cols = [
    { x: M, label: "MEDICATION" },
    { x: M + 170, label: "DOSAGE" },
    { x: M + 280, label: "PRESCRIBER" },
    { x: M + 400, label: "NEXT REFILL" },
  ];
  canvas.rect(M - 4, canvas.y - 14, PAGE_W - M * 2 + 8, 18, { fill: C_BRAND_DARK });
  cols.forEach((c) => canvas.text(c.label, c.x, canvas.y - 10, { size: 8, bold: true, color: rgb(1, 1, 1) }));
  canvas.y -= 22;

  active.forEach((rx, idx) => {
    canvas.ensure(32);
    if (idx % 2 === 0) canvas.rect(M - 4, canvas.y - 22, PAGE_W - M * 2 + 8, 26, { fill: C_BG_SOFT });
    canvas.text(rx.medName || "—", M, canvas.y - 6, { size: 10, bold: true, maxWidth: 166 });
    canvas.text(rx.dosage || "—", M + 170, canvas.y - 6, { size: 9, maxWidth: 106 });
    canvas.text(rx.prescriber || "—", M + 280, canvas.y - 6, { size: 9, maxWidth: 116 });
    const next = rxNextRefillDate(rx);
    canvas.text(next ? next.toLocaleDateString() : "—", M + 400, canvas.y - 6, { size: 9, maxWidth: 80 });
    canvas.y -= 26;
  });
}

// === QUESTIONS / THINGS TO BRING ===
function drawChecklist(canvas, appointment, conditions, hasMeds) {
  canvas.newPage("Checklist");
  canvas.heading("THINGS TO BRING & QUESTIONS TO ASK");

  canvas.sectionTitle("Bring with you");
  const bringList = [
    "This packet (printed or on your phone).",
    "Photo ID and any required appointment letter or check-in code.",
    hasMeds
      ? "Your current medication list — included in this packet on the previous page."
      : "A written list of every medication you currently take (name, dose, prescriber).",
    "Most recent medical records relevant to today's conditions, if not already on file.",
    "Names + contact info of any non-VA providers who have treated these conditions.",
    "A notebook or your phone to record what the examiner says.",
    "If applicable: hearing aids, glasses, cane, brace — bring assistive devices you actually use.",
  ];
  bringList.forEach((item) => canvas.bullet(item, { size: 9 }));

  canvas.y -= 6;
  canvas.sectionTitle("How to describe your conditions to the examiner");
  const tipList = [
    "Describe your WORST day, not your best. Examiners often ask about 'typical' — clarify that symptoms fluctuate.",
    "Be specific about FREQUENCY (how often) and DURATION (how long) of flare-ups.",
    "Connect symptoms to function: 'I can't lift more than X pounds without sharp pain,' 'I miss work N days/month.'",
    "Mention every medication, side effect, and assistive device you use.",
    "Do not minimize. Veterans often understate symptoms because they want to appear strong.",
    "Ask for a copy of the exam report (DBQ) after the appointment — you have the right to one.",
  ];
  tipList.forEach((item) => canvas.bullet(item, { size: 9 }));

  if (appointment?.type === "cp_exam") {
    canvas.y -= 6;
    canvas.sectionTitle("If this is a C&P (Compensation & Pension) exam");
    canvas.bullet("The examiner is NOT your treating doctor — they are evaluating your claim. Stick to facts.", { size: 9 });
    canvas.bullet("They will fill out a DBQ (Disability Benefits Questionnaire). The DBQ controls the rating decision.", { size: 9 });
    canvas.bullet("If they don't ask about a specific symptom you have, volunteer it. They cannot rate what isn't in the record.", { size: 9 });
    canvas.bullet("If they perform a brief range-of-motion test, ask whether they accounted for pain on motion and after repetitive use.", { size: 9 });
  }

  if (conditions.length > 0) {
    canvas.y -= 6;
    canvas.sectionTitle("Personal note space — write down what was said per condition");
    conditions.forEach((c) => {
      canvas.ensure(60);
      canvas.text(`${c.claimedAs || "Condition"} (DC ${c.code || "?"}):`, M, canvas.y, { size: 9, bold: true });
      canvas.y -= 14;
      canvas.line(M, canvas.y, PAGE_W - M, canvas.y, C_BORDER, 0.5);
      canvas.y -= 12;
      canvas.line(M, canvas.y, PAGE_W - M, canvas.y, C_BORDER, 0.5);
      canvas.y -= 12;
      canvas.line(M, canvas.y, PAGE_W - M, canvas.y, C_BORDER, 0.5);
      canvas.y -= 16;
    });
  }
}

export async function generateExamPacket({
  appointment,
  conditions = [],
  evidence = [],
  prescriptions = [],
  profile = {},
  workspaceTitle = "",
  workspace = null,
  master526ez = null,
  isFree = false,
}) {
  const doc = await PDFDocument.create();
  const font = await doc.embedFont(StandardFonts.Helvetica);
  const bold = await doc.embedFont(StandardFonts.HelveticaBold);
  const canvas = new Canvas(doc, font, bold);
  const waiverCtx = { workspace, master526ez };

  const info = {
    title: appointment?.title || "Day-of-Exam Packet",
    appointment,
    profile,
    workspaceTitle,
  };

  // If the appointment has linked issue IDs, prioritize those; otherwise include all
  const linked = appointment?.linkedIssueIds || [];
  const focused = linked.length
    ? conditions.filter((c) => linked.includes(c.id))
    : conditions;
  const finalConditions = focused.length ? focused : conditions;

  drawCover(canvas, info);
  drawConditionsOverview(canvas, finalConditions, evidence, waiverCtx);
  finalConditions.forEach((issue) => drawConditionDetail(canvas, issue, evidence, waiverCtx));
  const hasMeds = (prescriptions || []).some((rx) => !rx.archived);
  drawMedications(canvas, prescriptions);
  drawChecklist(canvas, appointment, finalConditions, hasMeds);

  // Header, footer, watermark on every page
  const totalPages = canvas.pages.length;
  canvas.pages.forEach((rec, idx) => {
    canvas.page = rec.page;
    canvas.section = rec.section;
    drawHeader(canvas, info);
    drawFooter(canvas, idx + 1, totalPages);
    drawWatermark(canvas, isFree);
  });

  const bytes = await doc.save();
  const blob = new Blob([bytes], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  const safeName = (appointment?.title || "exam").replace(/[^a-z0-9]+/gi, "_").slice(0, 50);
  const datePart = appointment?.datetime ? new Date(appointment.datetime).toISOString().slice(0, 10) : "appt";
  a.href = url;
  a.download = `ClaimOps_ExamPacket_${datePart}_${safeName}.pdf`;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 100);
  return { totalPages };
}
